import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  CHURCH_TAX_LABELS,
  CHURCH_TAXES,
  EMPLOYMENT_KIND_LABELS,
  EMPLOYMENT_KINDS,
  FLAT_PERIOD_LABELS,
  FLAT_PERIODS,
  formatCents,
  GENDER_LABELS,
  GENDERS,
  INSURANCE_KIND_LABELS,
  INSURANCE_KINDS,
  PAY_RHYTHM_LABELS,
  PAY_RHYTHMS,
  PAY_TYPE_LABELS,
  PAY_TYPES,
  PAYROLL_FIELD_LABELS,
  SENSITIVE_FIELDS,
  SENSITIVE_LABELS,
  type Compensation,
  type Employee,
  type EmployeeSecrets,
  type EmployeePayroll as PayrollData,
  type FlatPeriod,
  type PayrollView,
  type PayType,
} from '@kontor/shared';
import { api, ApiError } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { InfoHint } from '../../components/InfoHint';
import { bpText, centsText, parseBp, parseCents } from '../../money';
import { useSession } from '../../session';

const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const day = (d: string | null) => (d ? dateFormat.format(new Date(`${d}T12:00:00`)) : null);
const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);
const num = (text: string): number | null => {
  const t = text.trim().replace(',', '.');
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
};

/** „18,50 € pro Stunde“ oder „3.200,00 € pro Monat“ */
export const payText = (c: Pick<Compensation, 'payType' | 'amountCents' | 'flatPeriod'>) =>
  c.payType === 'hourly' ? `${formatCents(c.amountCents)} pro Stunde` : `${formatCents(c.amountCents)} ${FLAT_PERIOD_LABELS[c.flatPeriod ?? 'month']}`;
const surcharges = (c: Compensation) =>
  [c.surchargeNightBp ? `Nacht ${bpText(c.surchargeNightBp)} %` : null, c.surchargeSundayBp ? `Sonntag ${bpText(c.surchargeSundayBp)} %` : null, c.surchargeHolidayBp ? `Feiertag ${bpText(c.surchargeHolidayBp)} %` : null].filter(Boolean).join(', ');

/**
 * Lohn und Abrechnung eines Mitarbeiters: Vergütung (Stundensatz oder Pauschalbetrag, mit Verlauf) und die Lohnstammdaten für Steuer und Sozialversicherung. Wirkia rechnet
 * keinen Lohn ab, es hält die Angaben bereit. Die Karte erscheint nur mit dem Recht „Lohndaten“. Die Werte kommen erst nach einem Klick auf „Anzeigen“, das Ansehen
 * steht im Verlauf, und beim Wechsel des Mitarbeiters bleibt nichts stehen.
 */
export function EmployeePayroll({ employee, onChanged }: { employee: Employee; onChanged: () => void }) {
  const { can } = useSession();
  const [view, setView] = useState<PayrollView | null>(null);
  const [secrets, setSecrets] = useState<EmployeeSecrets | null>(null);
  const [editing, setEditing] = useState(false);
  const [adding, setAdding] = useState(false);

  // Lohnstammdaten und die sensiblen Personendaten kommen zusammen, jeder Abruf steht im Verlauf
  const reveal = useMutation({
    mutationFn: async () => {
      const [payroll, personal] = await Promise.all([
        api<{ data: PayrollView }>('POST', `/employees/${employee.id}/payroll/view`),
        api<{ data: EmployeeSecrets }>('POST', `/employees/${employee.id}/reveal`),
      ]);
      return { payroll: payroll.data, personal: personal.data };
    },
    onSuccess: ({ payroll, personal }) => {
      setView(payroll);
      setSecrets(personal);
      onChanged();
    },
  });
  useEffect(
    () => () => {
      setView(null);
      setSecrets(null);
    },
    [employee.id],
  );
  if (!can('hr.payroll')) return null;

  const hide = () => {
    setView(null);
    setSecrets(null);
    setEditing(false);
    setAdding(false);
  };
  /** Nach dem Speichern die sensiblen Werte neu laden, denn sie können sich geändert haben */
  const saved = (data: PayrollView) => {
    setView(data);
    void api<{ data: EmployeeSecrets }>('POST', `/employees/${employee.id}/reveal`).then((r) => setSecrets(r.data)).catch(() => setSecrets(null));
    setEditing(false);
    setAdding(false);
    onChanged();
  };

  return (
    <CollapsibleCard
      title="Lohn und Abrechnung"
      storageKey="employee-payroll"
      actions={
        view ? (
          <button type="button" className="btn btn--secondary btn--sm" onClick={hide}>Verbergen</button>
        ) : (
          <button type="button" className="btn btn--secondary btn--sm" disabled={reveal.isPending} onClick={() => reveal.mutate()}>{reveal.isPending ? 'Wird abgerufen …' : 'Anzeigen'}</button>
        )
      }
    >
      <p className="muted small">
        Vergütung, Steuermerkmale, Sozialversicherung und die sensiblen Personendaten (Geburtsdatum, Steuer- und Sozialversicherungsnummer, Bankverbindung) für die Lohnabrechnung.
        Wirkia rechnet keinen Lohn ab, es hält die Angaben für Ihr Lohnbüro oder den Steuerberater bereit. Jedes Anzeigen und Ändern steht ohne Werte im Verlauf.
      </p>
      {reveal.isError && <div className="alert alert--error" role="alert">{errorText(reveal.error, 'Die Lohndaten konnten nicht abgerufen werden.')}</div>}
      {!view && !reveal.isError && <p className="muted">Die Lohndaten sind verborgen. „Anzeigen“ ruft sie ab.</p>}
      {view && (
        <>
          {view.warnings.length > 0 && (
            <div className="alert" role="status">
              <strong>Zur Prüfung:</strong>
              <ul className="payroll-warnings">{view.warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            </div>
          )}
          <Compensation_ employee={employee} view={view} adding={adding} setAdding={setAdding} onSaved={saved} />
          {editing ? (
            <PayrollForm employee={employee} data={view.payroll} secrets={secrets} onCancel={() => setEditing(false)} onSaved={saved} />
          ) : (
            <PayrollFacts data={view.payroll} secrets={secrets} onEdit={() => setEditing(true)} />
          )}
        </>
      )}
    </CollapsibleCard>
  );
}

// ---------------------------------------------------------------------------
// Vergütung
// ---------------------------------------------------------------------------

function Compensation_({ employee, view, adding, setAdding, onSaved }: { employee: Employee; view: PayrollView; adding: boolean; setAdding: (v: boolean) => void; onSaved: (v: PayrollView) => void }) {
  const c = view.current;
  const future = view.compensations.filter((x) => x.validFrom > today());
  return (
    <section className="payroll-block" aria-label="Vergütung">
      <div className="section-head">
        <h3>Vergütung</h3>
        {!adding && <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAdding(true)}>{c ? 'Neue Vergütung eintragen' : 'Vergütung eintragen'}</button>}
      </div>
      {c ? (
        <div className="payroll-pay">
          <strong className="payroll-pay__main">{payText(c)}</strong>
          <span className="muted">gültig seit {day(c.validFrom)}</span>
          {view.monthlyEstimateCents !== null && <span className="muted">≈ {formatCents(view.monthlyEstimateCents)} brutto im Monat{c.payType === 'hourly' && employee.weeklyHours ? ` bei ${employee.weeklyHours.toLocaleString('de-DE')} Std. pro Woche` : ''}</span>}
          {(c.annualBonusCents > 0 || surcharges(c)) && (
            <span className="muted">
              {c.annualBonusCents > 0 ? `Jahressonderzahlung ${formatCents(c.annualBonusCents)}` : ''}
              {c.annualBonusCents > 0 && surcharges(c) ? ' · ' : ''}
              {surcharges(c) ? `Zuschläge: ${surcharges(c)}` : ''}
            </span>
          )}
          {c.note && <span className="muted">{c.note}</span>}
        </div>
      ) : (
        <p className="muted">Noch keine Vergütung eingetragen.</p>
      )}
      {future.length > 0 && <p className="muted small">Geplant: {future.map((f) => `${payText(f)} ab ${day(f.validFrom)}`).join(', ')}.</p>}
      {adding && <CompensationForm employee={employee} previous={c} onCancel={() => setAdding(false)} onSaved={onSaved} />}
      {view.compensations.length > 1 && (
        <details className="payroll-history">
          <summary>Verlauf der Vergütung ({view.compensations.length})</summary>
          <table className="table table--cards">
            <thead>
              <tr>
                <th>Gültig ab</th>
                <th>Vergütung</th>
                <th>Zuschläge und Sonderzahlung</th>
                <th>Eingetragen</th>
              </tr>
            </thead>
            <tbody>
              {view.compensations.map((x) => (
                <tr key={x.id}>
                  <td data-label="Gültig ab">{day(x.validFrom)}</td>
                  <td data-label="Vergütung">{payText(x)}</td>
                  <td data-label="Zuschläge">{[surcharges(x), x.annualBonusCents > 0 ? `Sonderzahlung ${formatCents(x.annualBonusCents)}` : ''].filter(Boolean).join(' · ') || '–'}</td>
                  <td data-label="Eingetragen">{day(x.createdAt.slice(0, 10))}{x.createdByName ? ` von ${x.createdByName}` : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </section>
  );
}

function CompensationForm({ employee, previous, onCancel, onSaved }: { employee: Employee; previous: Compensation | null; onCancel: () => void; onSaved: (v: PayrollView) => void }) {
  const [validFrom, setValidFrom] = useState(today());
  const [payType, setPayType] = useState<PayType>(previous?.payType ?? 'hourly');
  const [period, setPeriod] = useState<FlatPeriod>(previous?.flatPeriod ?? 'month');
  const [amount, setAmount] = useState(previous ? centsText(previous.amountCents) : '');
  const [bonus, setBonus] = useState(previous?.annualBonusCents ? centsText(previous.annualBonusCents) : '');
  const [night, setNight] = useState(previous?.surchargeNightBp ? bpText(previous.surchargeNightBp) : '');
  const [sunday, setSunday] = useState(previous?.surchargeSundayBp ? bpText(previous.surchargeSundayBp) : '');
  const [holiday, setHoliday] = useState(previous?.surchargeHolidayBp ? bpText(previous.surchargeHolidayBp) : '');
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const cents = parseCents(amount);
      if (cents === null || cents <= 0) throw new ApiError(422, 'validation_failed', 'Bitte einen Betrag größer als 0 angeben.');
      const pct = (t: string) => (t.trim() ? parseBp(t) : 0);
      const bonusCents = bonus.trim() ? parseCents(bonus) : 0;
      if ([pct(night), pct(sunday), pct(holiday), bonusCents].includes(null)) throw new ApiError(422, 'validation_failed', 'Bitte Zuschläge und Sonderzahlung als Zahl angeben.');
      return (
        await api<{ data: PayrollView }>('POST', `/employees/${employee.id}/payroll/compensations`, {
          validFrom,
          payType,
          amountCents: cents,
          flatPeriod: payType === 'flat' ? period : null,
          annualBonusCents: bonusCents,
          surchargeNightBp: pct(night),
          surchargeSundayBp: pct(sunday),
          surchargeHolidayBp: pct(holiday),
          note: note.trim() || null,
          belowMinimumReason: reason.trim() || undefined,
        })
      ).data;
    },
    onSuccess: onSaved,
    onError: (e) => setError(errorText(e, 'Die Vergütung konnte nicht gespeichert werden.')),
  });

  return (
    <form className="payment-form" onSubmit={(e: FormEvent) => { e.preventDefault(); setError(null); save.mutate(); }} noValidate>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <div className="form-grid">
        <label className="field">
          <span>Gültig ab</span>
          <input className="input" type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
        </label>
        <label className="field">
          <span className="invoice-label">Art der Vergütung<InfoHint label="Art der Vergütung">Stundensatz: Der Lohn richtet sich nach den geleisteten Stunden. Pauschalbetrag: ein fester Betrag je Monat, Woche oder Jahr, zum Beispiel ein Monatsgehalt. Eine Änderung tragen Sie als neue Vergütung mit neuem Datum ein, die alte bleibt im Verlauf stehen.</InfoHint></span>
          <select className="input" value={payType} onChange={(e) => setPayType(e.target.value as PayType)}>
            {PAY_TYPES.map((t) => <option key={t} value={t}>{PAY_TYPE_LABELS[t]}</option>)}
          </select>
        </label>
        <label className="field">
          <span>{payType === 'hourly' ? 'Stundensatz € (brutto)' : 'Betrag € (brutto)'}</span>
          <input className="input num" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" />
        </label>
        {payType === 'flat' && (
          <label className="field">
            <span>Gilt</span>
            <select className="input" value={period} onChange={(e) => setPeriod(e.target.value as FlatPeriod)}>
              {FLAT_PERIODS.map((p) => <option key={p} value={p}>{FLAT_PERIOD_LABELS[p]}</option>)}
            </select>
          </label>
        )}
        <label className="field">
          <span className="invoice-label">Jahressonderzahlung €<InfoHint label="Jahressonderzahlung">Zum Beispiel Weihnachts- oder Urlaubsgeld, brutto je Jahr. Leer, wenn es keine gibt.</InfoHint></span>
          <input className="input num" inputMode="decimal" value={bonus} onChange={(e) => setBonus(e.target.value)} placeholder="0,00" />
        </label>
      </div>
      <h4 className="payroll-sub">Zuschläge auf den Grundlohn (in Prozent)</h4>
      <div className="form-grid">
        <label className="field">
          <span>Nacht</span>
          <input className="input num" inputMode="decimal" value={night} onChange={(e) => setNight(e.target.value)} placeholder="0" />
        </label>
        <label className="field">
          <span>Sonntag</span>
          <input className="input num" inputMode="decimal" value={sunday} onChange={(e) => setSunday(e.target.value)} placeholder="0" />
        </label>
        <label className="field">
          <span className="invoice-label">Feiertag<InfoHint label="Zuschläge">Zuschläge für Nacht-, Sonntags- und Feiertagsarbeit sind bis zu bestimmten Grenzen steuerfrei (§ 3b EStG). Das prüft das Lohnbüro. Hier steht nur, was vereinbart ist.</InfoHint></span>
          <input className="input num" inputMode="decimal" value={holiday} onChange={(e) => setHoliday(e.target.value)} placeholder="0" />
        </label>
      </div>
      {error && /Mindestlohn/.test(error) && (
        <label className="field">
          <span className="invoice-label">Begründung für den Lohn unter dem Mindestlohn<InfoHint label="Ausnahme vom Mindestlohn">Nur wenn eine gesetzliche Ausnahme gilt, zum Beispiel Langzeitarbeitslose in den ersten sechs Monaten oder Minderjährige ohne Berufsausbildung. Die Begründung steht dauerhaft in der Vergütung und im Verlauf.</InfoHint></span>
          <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="Welche Ausnahme gilt?" />
        </label>
      )}
      <label className="field">
        <span>Notiz</span>
        <input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} placeholder="Grund der Änderung, zum Beispiel Gehaltserhöhung" />
      </label>
      <div className="form-actions">
        <button className="btn btn--primary btn--sm" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Vergütung speichern'}</button>
        <button className="btn btn--secondary btn--sm" type="button" onClick={onCancel}>Abbrechen</button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Lohnstammdaten ansehen
// ---------------------------------------------------------------------------

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{children === null || children === undefined || children === '' || children === false ? <span className="muted">–</span> : children}</dd>
    </div>
  );
}
const yesNo = (v: boolean) => (v ? 'Ja' : 'Nein');
const money = (c: number | null) => (c === null ? null : formatCents(c));

function PayrollFacts({ data: p, secrets, onEdit }: { data: PayrollData; secrets: EmployeeSecrets | null; onEdit: () => void }) {
  return (
    <section className="payroll-block" aria-label="Lohnstammdaten">
      <div className="section-head">
        <h3>Lohnstammdaten</h3>
        <button type="button" className="btn btn--secondary btn--sm" onClick={onEdit}>Bearbeiten</button>
      </div>
      <h4 className="payroll-sub">Persönliche Angaben (sensibel)</h4>
      <dl className="facts payroll-facts">
        {SENSITIVE_FIELDS.map((f) => (
          <Row key={f} label={SENSITIVE_LABELS[f]}>{secrets?.[f] ? (f === 'birthDate' ? day(secrets[f]) : <span className="secret-value">{secrets[f]}</span>) : null}</Row>
        ))}
      </dl>
      <h4 className="payroll-sub">Beschäftigung</h4>
      <dl className="facts payroll-facts">
        <Row label="Beschäftigungsart">{p.employmentKind ? EMPLOYMENT_KIND_LABELS[p.employmentKind] : null}</Row>
        <Row label="Zahlungsrhythmus">{p.payRhythm ? PAY_RHYTHM_LABELS[p.payRhythm] : 'Vorgabe des Betriebs'}</Row>
        <Row label="Hauptbeschäftigung">{yesNo(p.mainEmployment)}</Row>
        <Row label="Probezeit bis">{day(p.probationUntil)}</Row>
        <Row label="Befristet bis">{day(p.contractEndsOn)}</Row>
        <Row label="Kostenstelle">{p.costCenter}</Row>
      </dl>
      <h4 className="payroll-sub">Steuer</h4>
      <dl className="facts payroll-facts">
        <Row label="Steuerklasse">{p.taxClass}</Row>
        <Row label="Faktor">{p.taxFactor?.toLocaleString('de-DE', { minimumFractionDigits: 3 })}</Row>
        <Row label="Kinderfreibeträge">{p.childAllowance?.toLocaleString('de-DE', { minimumFractionDigits: 1 })}</Row>
        <Row label="Kirchensteuer">{p.churchTax ? CHURCH_TAX_LABELS[p.churchTax] : null}</Row>
        {(p.employmentKind === 'mini_job' || p.employmentKind === 'short_term') && <Row label="Pauschalsteuer">{p.minijobFlatTax ? 'Ja, vom Arbeitgeber pauschal' : 'Nein, nach Lohnsteuermerkmalen'}</Row>}
      </dl>
      <h4 className="payroll-sub">Sozialversicherung</h4>
      <dl className="facts payroll-facts">
        <Row label="Krankenversicherung">{p.insuranceKind ? INSURANCE_KIND_LABELS[p.insuranceKind] : null}</Row>
        <Row label="Krankenkasse">{p.healthInsuranceName}</Row>
        <Row label="Betriebsnummer der Kasse">{p.healthInsuranceNumber}</Row>
        <Row label="Zusatzbeitrag">{p.healthAdditionalBp === null ? null : `${bpText(p.healthAdditionalBp) || '0'} %`}</Row>
        {p.insuranceKind === 'private' && <Row label="Zuschuss des Arbeitgebers">{money(p.privateSubsidyCents)}</Row>}
        <Row label="Anzahl der Kinder">{p.childrenCount}</Row>
        <Row label="Personengruppe">{p.personGroup}</Row>
        <Row label="Beitragsgruppe">{p.contributionGroup}</Row>
        <Row label="Tätigkeitsschlüssel">{p.activityKey}</Row>
        <Row label="Von der Rentenversicherung befreit">{yesNo(p.rvExempt)}</Row>
        <Row label="Schwerbehinderung">{yesNo(p.disabled)}</Row>
      </dl>
      <h4 className="payroll-sub">Angaben zur Person für die Meldungen</h4>
      <dl className="facts payroll-facts">
        <Row label="Geschlecht">{p.gender ? GENDER_LABELS[p.gender] : null}</Row>
        <Row label="Geburtsname">{p.birthName}</Row>
        <Row label="Geburtsort">{p.birthPlace}</Row>
        <Row label="Staatsangehörigkeit">{p.nationality}</Row>
      </dl>
      <h4 className="payroll-sub">Zusätze</h4>
      <dl className="facts payroll-facts">
        <Row label="VWL Arbeitnehmer">{money(p.vwlEmployeeCents)}</Row>
        <Row label="VWL Arbeitgeber">{money(p.vwlEmployerCents)}</Row>
        <Row label="Altersvorsorge Arbeitnehmer">{money(p.pensionEmployeeCents)}</Row>
        <Row label="Altersvorsorge Arbeitgeber">{money(p.pensionEmployerCents)}</Row>
        <Row label="Pfändung">{p.garnishment ? `Ja${p.garnishmentNote ? `: ${p.garnishmentNote}` : ''}` : 'Nein'}</Row>
        <Row label="Notiz">{p.note}</Row>
      </dl>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Lohnstammdaten bearbeiten
// ---------------------------------------------------------------------------

interface Form {
  employmentKind: string;
  payRhythm: string;
  mainEmployment: boolean;
  probationUntil: string;
  contractEndsOn: string;
  costCenter: string;
  taxClass: string;
  taxFactor: string;
  childAllowance: string;
  churchTax: string;
  minijobFlatTax: boolean;
  insuranceKind: string;
  healthInsuranceName: string;
  healthInsuranceNumber: string;
  healthAdditional: string;
  privateSubsidy: string;
  childrenCount: string;
  personGroup: string;
  contributionGroup: string;
  activityKey: string;
  rvExempt: boolean;
  disabled: boolean;
  gender: string;
  birthName: string;
  birthPlace: string;
  nationality: string;
  vwlEmployee: string;
  vwlEmployer: string;
  pensionEmployee: string;
  pensionEmployer: string;
  garnishment: boolean;
  garnishmentNote: string;
  note: string;
}

const toForm = (p: PayrollData): Form => ({
  employmentKind: p.employmentKind ?? '',
  payRhythm: p.payRhythm ?? '',
  mainEmployment: p.mainEmployment,
  probationUntil: p.probationUntil ?? '',
  contractEndsOn: p.contractEndsOn ?? '',
  costCenter: p.costCenter ?? '',
  taxClass: p.taxClass === null ? '' : String(p.taxClass),
  taxFactor: p.taxFactor === null ? '' : String(p.taxFactor).replace('.', ','),
  childAllowance: p.childAllowance === null ? '' : String(p.childAllowance).replace('.', ','),
  churchTax: p.churchTax ?? '',
  minijobFlatTax: p.minijobFlatTax,
  insuranceKind: p.insuranceKind ?? '',
  healthInsuranceName: p.healthInsuranceName ?? '',
  healthInsuranceNumber: p.healthInsuranceNumber ?? '',
  healthAdditional: p.healthAdditionalBp === null ? '' : bpText(p.healthAdditionalBp) || '0',
  privateSubsidy: p.privateSubsidyCents === null ? '' : centsText(p.privateSubsidyCents),
  childrenCount: p.childrenCount === null ? '' : String(p.childrenCount),
  personGroup: p.personGroup ?? '',
  contributionGroup: p.contributionGroup ?? '',
  activityKey: p.activityKey ?? '',
  rvExempt: p.rvExempt,
  disabled: p.disabled,
  gender: p.gender ?? '',
  birthName: p.birthName ?? '',
  birthPlace: p.birthPlace ?? '',
  nationality: p.nationality ?? '',
  vwlEmployee: p.vwlEmployeeCents === null ? '' : centsText(p.vwlEmployeeCents),
  vwlEmployer: p.vwlEmployerCents === null ? '' : centsText(p.vwlEmployerCents),
  pensionEmployee: p.pensionEmployeeCents === null ? '' : centsText(p.pensionEmployeeCents),
  pensionEmployer: p.pensionEmployerCents === null ? '' : centsText(p.pensionEmployerCents),
  garnishment: p.garnishment,
  garnishmentNote: p.garnishmentNote ?? '',
  note: p.note ?? '',
});

const SECRET_PLACEHOLDERS: Record<(typeof SENSITIVE_FIELDS)[number], string> = {
  birthDate: '',
  taxNumber: 'z. B. 12/345/67890',
  taxId: 'elf Ziffern, z. B. 86095742719',
  socialSecurityNumber: 'z. B. 12 140488 A 123',
  iban: 'z. B. DE89 3704 0044 0532 0130 00',
  bic: 'z. B. COBADEFFXXX (8 oder 11 Zeichen)',
};

function PayrollForm({ employee, data, secrets, onCancel, onSaved }: { employee: Employee; data: PayrollData; secrets: EmployeeSecrets | null; onCancel: () => void; onSaved: (v: PayrollView) => void }) {
  const [f, setF] = useState<Form>(() => toForm(data));
  const [personal, setPersonal] = useState<Record<string, string>>(() => Object.fromEntries(SENSITIVE_FIELDS.map((k) => [k, secrets?.[k] ?? ''])));
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof Form>(key: K, value: Form[K]) => setF((x) => ({ ...x, [key]: value }));
  const err = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  const save = useMutation({
    mutationFn: async () => {
      const text = (v: string) => v.trim() || null;
      const cents = (v: string, label: string) => {
        if (!v.trim()) return null;
        const c = parseCents(v);
        if (c === null || c < 0) throw new ApiError(422, 'validation_failed', 'Bitte prüfen Sie Ihre Eingaben.', { [label]: 'Bitte einen Betrag angeben.' });
        return c;
      };
      const parsedFactor = num(f.taxFactor);
      const taxFactor = parsedFactor === 0 ? null : parsedFactor;
      const childAllowance = num(f.childAllowance);
      const additional = f.healthAdditional.trim() ? parseBp(f.healthAdditional) : null;
      const children = num(f.childrenCount);
      const taxClass = num(f.taxClass);
      if ([taxFactor, childAllowance, children, taxClass].some((n) => Number.isNaN(n)) || (f.healthAdditional.trim() && additional === null)) {
        throw new ApiError(422, 'validation_failed', 'Bitte prüfen Sie Ihre Eingaben.', { _: 'Bitte Zahlen in den Zahlenfeldern angeben.' });
      }
      return (
        await api<{ data: PayrollView }>('PUT', `/employees/${employee.id}/payroll`, {
          ...Object.fromEntries(SENSITIVE_FIELDS.map((k) => [k, text(personal[k] ?? '')])),
          employmentKind: text(f.employmentKind),
          payRhythm: text(f.payRhythm),
          mainEmployment: f.mainEmployment,
          probationUntil: text(f.probationUntil),
          contractEndsOn: text(f.contractEndsOn),
          costCenter: text(f.costCenter),
          taxClass,
          taxFactor,
          childAllowance,
          churchTax: text(f.churchTax),
          minijobFlatTax: f.minijobFlatTax,
          insuranceKind: text(f.insuranceKind),
          healthInsuranceName: text(f.healthInsuranceName),
          healthInsuranceNumber: text(f.healthInsuranceNumber),
          healthAdditionalBp: additional,
          privateSubsidyCents: cents(f.privateSubsidy, 'privateSubsidyCents'),
          childrenCount: children,
          personGroup: text(f.personGroup),
          contributionGroup: text(f.contributionGroup),
          activityKey: text(f.activityKey),
          rvExempt: f.rvExempt,
          disabled: f.disabled,
          gender: text(f.gender),
          birthName: text(f.birthName),
          birthPlace: text(f.birthPlace),
          nationality: text(f.nationality),
          vwlEmployeeCents: cents(f.vwlEmployee, 'vwlEmployeeCents'),
          vwlEmployerCents: cents(f.vwlEmployer, 'vwlEmployerCents'),
          pensionEmployeeCents: cents(f.pensionEmployee, 'pensionEmployeeCents'),
          pensionEmployerCents: cents(f.pensionEmployer, 'pensionEmployerCents'),
          garnishment: f.garnishment,
          garnishmentNote: text(f.garnishmentNote),
          note: text(f.note),
        })
      ).data;
    },
    onSuccess: onSaved,
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setError(e.fields._ ?? 'Bitte prüfen Sie die markierten Felder.');
      } else setError(errorText(e, 'Die Lohndaten konnten nicht gespeichert werden.'));
    },
  });

  const mini = f.employmentKind === 'mini_job' || f.employmentKind === 'short_term';
  const hint = (label: string, text: string) => <InfoHint label={label}>{text}</InfoHint>;

  return (
    <form className="payroll-block payroll-form" onSubmit={(e: FormEvent) => { e.preventDefault(); setError(null); setErrors({}); save.mutate(); }} noValidate>
      <div className="section-head"><h3>Lohnstammdaten bearbeiten</h3></div>
      {error && (
        <div className="alert alert--error" role="alert">
          {error}
          {Object.keys(errors).filter((k) => k !== '_').length > 0 && (
            <ul className="payroll-warnings">
              {Object.entries(errors).filter(([k]) => k !== '_').map(([k, m]) => <li key={k}><strong>{PAYROLL_FIELD_LABELS[k] ?? k}:</strong> {m}</li>)}
            </ul>
          )}
        </div>
      )}

      <h4 className="payroll-sub">Persönliche Angaben (sensibel)</h4>
      <div className="form-grid">
        {SENSITIVE_FIELDS.map((k) => (
          <label key={k} className="field">
            <span>{SENSITIVE_LABELS[k]}</span>
            <input
              className="input"
              type={k === 'birthDate' ? 'date' : 'text'}
              value={personal[k] ?? ''}
              onChange={(e) => setPersonal((x) => ({ ...x, [k]: e.target.value }))}
              placeholder={SECRET_PLACEHOLDERS[k]}
              autoComplete="off"
              aria-invalid={!!errors[k]}
            />
            {err(k)}
          </label>
        ))}
      </div>

      <h4 className="payroll-sub">Beschäftigung</h4>
      <div className="form-grid">
        <label className="field">
          <span>Beschäftigungsart</span>
          <select className="input" value={f.employmentKind} onChange={(e) => set('employmentKind', e.target.value)}>
            <option value="">Nicht angegeben</option>
            {EMPLOYMENT_KINDS.map((k) => <option key={k} value={k}>{EMPLOYMENT_KIND_LABELS[k]}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="invoice-label">Zahlungsrhythmus{hint('Zahlungsrhythmus', 'Wie oft der Lohn ausgezahlt wird. Leer gilt die Vorgabe des Betriebs aus der Einrichtung der Löhne. „Nach Vereinbarung“ ist erlaubt, solange der Lohn spätestens am letzten Bankarbeitstag des Folgemonats ausgezahlt wird (Mindestlohngesetz). Auszubildende werden spätestens am letzten Arbeitstag des Monats bezahlt.')}</span>
          <select className="input" value={f.payRhythm} onChange={(e) => set('payRhythm', e.target.value)} aria-invalid={!!errors.payRhythm}>
            <option value="">Vorgabe des Betriebs</option>
            {PAY_RHYTHMS.map((r) => <option key={r} value={r}>{PAY_RHYTHM_LABELS[r]}</option>)}
          </select>
          {err('payRhythm')}
        </label>
        <label className="field">
          <span>Probezeit bis</span>
          <input className="input" type="date" value={f.probationUntil} onChange={(e) => set('probationUntil', e.target.value)} aria-invalid={!!errors.probationUntil} />
          {err('probationUntil')}
        </label>
        <label className="field">
          <span>Befristet bis</span>
          <input className="input" type="date" value={f.contractEndsOn} onChange={(e) => set('contractEndsOn', e.target.value)} aria-invalid={!!errors.contractEndsOn} />
          {err('contractEndsOn')}
        </label>
        <label className="field">
          <span className="invoice-label">Kostenstelle{hint('Kostenstelle', 'Ordnet die Personalkosten einer Abteilung oder einem Projekt zu. Leer, wenn Sie keine Kostenstellen führen.')}</span>
          <input className="input" value={f.costCenter} maxLength={40} onChange={(e) => set('costCenter', e.target.value)} />
        </label>
      </div>
      <label className="check">
        <input type="checkbox" checked={f.mainEmployment} onChange={(e) => set('mainEmployment', e.target.checked)} />
        <span>Hauptbeschäftigung{hint('Hauptbeschäftigung', 'Bei mehreren Arbeitsverhältnissen ist nur eines die Hauptbeschäftigung. Für das zweite gilt in der Regel Steuerklasse VI. Haken weg, wenn dieser Mitarbeiter hier nur nebenbei arbeitet.')}</span>
      </label>

      <h4 className="payroll-sub">Steuer</h4>
      <div className="form-grid">
        <label className="field">
          <span className="invoice-label">Steuerklasse{hint('Steuerklasse', 'I: ledig oder geschieden. II: alleinerziehend. III: verheiratet, der Partner hat Klasse V. IV: verheiratet, beide verdienen ähnlich. V: verheiratet, der Partner hat Klasse III. VI: zweites Arbeitsverhältnis. Die Steuerklasse steht in den elektronischen Lohnsteuermerkmalen (ELStAM), die Ihr Lohnbüro beim Finanzamt abruft.')}</span>
          <select className="input" value={f.taxClass} onChange={(e) => set('taxClass', e.target.value)} aria-invalid={!!errors.taxClass}>
            <option value="">Nicht angegeben</option>
            {[1, 2, 3, 4, 5, 6].map((c) => <option key={c} value={c}>{['', 'I', 'II', 'III', 'IV', 'V', 'VI'][c]}</option>)}
          </select>
          {err('taxClass')}
        </label>
        <label className="field">
          <span className="invoice-label">Faktor{hint('Faktor', 'Nur bei Steuerklasse IV mit Faktor (IV/IV mit Faktorverfahren). Eine Zahl zwischen 0 und 1, zum Beispiel 0,876. Sonst leer.')}</span>
          <input className="input num" inputMode="decimal" value={f.taxFactor} onChange={(e) => set('taxFactor', e.target.value)} placeholder="0,000" aria-invalid={!!errors.taxFactor} />
          {err('taxFactor')}
        </label>
        <label className="field">
          <span className="invoice-label">Kinderfreibeträge{hint('Kinderfreibeträge', 'Ein Kind zählt mit 0,5 je Elternteil, mit beiden Elternteilen 1,0. Steht in den Lohnsteuermerkmalen (ELStAM).')}</span>
          <input className="input num" inputMode="decimal" value={f.childAllowance} onChange={(e) => set('childAllowance', e.target.value)} placeholder="0,0" aria-invalid={!!errors.childAllowance} />
          {err('childAllowance')}
        </label>
        <label className="field">
          <span className="invoice-label">Kirchensteuer{hint('Kirchensteuer', 'Die Religionszugehörigkeit laut Lohnsteuermerkmalen. „Keine“, wenn der Mitarbeiter keiner steuererhebenden Religionsgemeinschaft angehört.')}</span>
          <select className="input" value={f.churchTax} onChange={(e) => set('churchTax', e.target.value)}>
            <option value="">Nicht angegeben</option>
            {CHURCH_TAXES.map((c) => <option key={c} value={c}>{CHURCH_TAX_LABELS[c]}</option>)}
          </select>
        </label>
      </div>
      {mini && (
        <label className="check">
          <input type="checkbox" checked={f.minijobFlatTax} onChange={(e) => set('minijobFlatTax', e.target.checked)} />
          <span>Lohnsteuer pauschal vom Arbeitgeber{hint('Pauschalsteuer', 'Beim Minijob kann der Arbeitgeber die Lohnsteuer mit 2 % pauschal abführen. Dann braucht es keine Steuerklasse. Haken weg, wenn nach den Lohnsteuermerkmalen des Mitarbeiters versteuert wird.')}</span>
        </label>
      )}

      <h4 className="payroll-sub">Sozialversicherung</h4>
      <div className="form-grid">
        <label className="field">
          <span>Krankenversicherung</span>
          <select className="input" value={f.insuranceKind} onChange={(e) => set('insuranceKind', e.target.value)}>
            <option value="">Nicht angegeben</option>
            {INSURANCE_KINDS.map((k) => <option key={k} value={k}>{INSURANCE_KIND_LABELS[k]}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Krankenkasse</span>
          <input className="input" value={f.healthInsuranceName} maxLength={120} onChange={(e) => set('healthInsuranceName', e.target.value)} placeholder="zum Beispiel AOK Bayern" />
        </label>
        <label className="field">
          <span className="invoice-label">Betriebsnummer der Kasse{hint('Betriebsnummer der Krankenkasse', 'Achtstellige Nummer, unter der die Krankenkasse die Meldungen entgegennimmt. Sie steht auf der Mitgliedsbescheinigung und bei der Kasse im Internet.')}</span>
          <input className="input" inputMode="numeric" maxLength={8} value={f.healthInsuranceNumber} onChange={(e) => set('healthInsuranceNumber', e.target.value)} aria-invalid={!!errors.healthInsuranceNumber} />
          {err('healthInsuranceNumber')}
        </label>
        <label className="field">
          <span className="invoice-label">Zusatzbeitrag %{hint('Zusatzbeitrag', 'Nur der Zusatzbeitrag der Krankenkasse, zum Beispiel 2,9 %, nicht der gesamte Beitragssatz mit dem allgemeinen Anteil. Die Kasse legt ihn fest, er steht auf ihrer Internetseite und ändert sich meist zum Jahreswechsel. Bei Privatversicherten leer lassen.')}</span>
          <input className="input num" inputMode="decimal" value={f.healthAdditional} onChange={(e) => set('healthAdditional', e.target.value)} placeholder="zum Beispiel 2,9" aria-invalid={!!errors.healthAdditionalBp} />
          {err('healthAdditionalBp')}
        </label>
        {f.insuranceKind === 'private' && (
          <label className="field">
            <span className="invoice-label">Zuschuss des Arbeitgebers €{hint('Zuschuss', 'Den Beitrag zur privaten Krankenversicherung bezuschusst der Arbeitgeber bis zu einer Höchstgrenze. Tragen Sie den monatlichen Zuschuss ein.')}</span>
            <input className="input num" inputMode="decimal" value={f.privateSubsidy} onChange={(e) => set('privateSubsidy', e.target.value)} placeholder="0,00" aria-invalid={!!errors.privateSubsidyCents} />
            {err('privateSubsidyCents')}
          </label>
        )}
        <label className="field">
          <span className="invoice-label">Anzahl der Kinder{hint('Anzahl der Kinder', 'Die Zahl der Kinder unter 25 Jahren, gemeint sind alle leiblichen und angenommenen Kinder. Sie bestimmt den Beitrag zur Pflegeversicherung: Kinderlose ab 23 Jahren zahlen einen Zuschlag (dann 0 eintragen), ab dem zweiten Kind sinkt der Beitrag. Das Feld „Kinderfreibeträge“ gehört zur Lohnsteuer und ersetzt diese Angabe nicht.')}</span>
          <input className="input num" inputMode="numeric" value={f.childrenCount} onChange={(e) => set('childrenCount', e.target.value)} placeholder="0" aria-invalid={!!errors.childrenCount} />
          {err('childrenCount')}
        </label>
        <label className="field">
          <span className="invoice-label">Personengruppe{hint('Personengruppenschlüssel', 'Dreistellig. 101: normale Beschäftigung, 102: Auszubildende, 109: geringfügig entlohnt (Minijob), 110: kurzfristig beschäftigt. Weitere Schlüssel kennt Ihr Lohnbüro.')}</span>
          <input className="input" inputMode="numeric" maxLength={3} value={f.personGroup} onChange={(e) => set('personGroup', e.target.value)} placeholder="101" aria-invalid={!!errors.personGroup} />
          {err('personGroup')}
        </label>
        <label className="field">
          <span className="invoice-label">Beitragsgruppe{hint('Beitragsgruppenschlüssel', 'Vierstellig, je eine Ziffer für Kranken-, Renten-, Arbeitslosen- und Pflegeversicherung. 1111: volle Beiträge in allen vier Zweigen. 6500: Minijob mit pauschalen Beiträgen. Im Zweifel fragen Sie Ihr Lohnbüro.')}</span>
          <input className="input" inputMode="numeric" maxLength={4} value={f.contributionGroup} onChange={(e) => set('contributionGroup', e.target.value)} placeholder="1111" aria-invalid={!!errors.contributionGroup} />
          {err('contributionGroup')}
        </label>
        <label className="field">
          <span className="invoice-label">Tätigkeitsschlüssel{hint('Tätigkeitsschlüssel', 'Neunstelliger Schlüssel der Bundesagentur für Arbeit für Beruf, Schulbildung, Ausbildung, Arbeitnehmerüberlassung und Vertragsform. Das Lohnbüro braucht ihn für die Meldungen an die Sozialversicherung.')}</span>
          <input className="input" inputMode="numeric" maxLength={9} value={f.activityKey} onChange={(e) => set('activityKey', e.target.value)} aria-invalid={!!errors.activityKey} />
          {err('activityKey')}
        </label>
      </div>
      <div className="payroll-checks">
        <label className="check">
          <input type="checkbox" checked={f.rvExempt} onChange={(e) => set('rvExempt', e.target.checked)} />
          <span>Von der Rentenversicherung befreit{hint('Befreiung', 'Minijobber können sich auf Antrag von der Rentenversicherungspflicht befreien lassen. Auch bestimmte Berufsgruppen sind befreit.')}</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={f.disabled} onChange={(e) => set('disabled', e.target.checked)} />
          <span>Schwerbehinderung</span>
        </label>
      </div>

      <h4 className="payroll-sub">Angaben zur Person für die Meldungen</h4>
      <div className="form-grid">
        <label className="field">
          <span>Geschlecht</span>
          <select className="input" value={f.gender} onChange={(e) => set('gender', e.target.value)}>
            <option value="">Nicht angegeben</option>
            {GENDERS.map((g) => <option key={g} value={g}>{GENDER_LABELS[g]}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Geburtsname</span>
          <input className="input" value={f.birthName} maxLength={80} onChange={(e) => set('birthName', e.target.value)} />
        </label>
        <label className="field">
          <span>Geburtsort</span>
          <input className="input" value={f.birthPlace} maxLength={80} onChange={(e) => set('birthPlace', e.target.value)} />
        </label>
        <label className="field">
          <span>Staatsangehörigkeit</span>
          <input className="input" value={f.nationality} maxLength={60} onChange={(e) => set('nationality', e.target.value)} />
        </label>
      </div>

      <h4 className="payroll-sub">Zusätze</h4>
      <div className="form-grid">
        <label className="field">
          <span className="invoice-label">VWL Arbeitnehmer € (monatlich){hint('Vermögenswirksame Leistungen', 'Der Betrag, den der Mitarbeiter monatlich in einen Sparvertrag einzahlt. Der Arbeitgeber kann einen Zuschuss geben.')}</span>
          <input className="input num" inputMode="decimal" value={f.vwlEmployee} onChange={(e) => set('vwlEmployee', e.target.value)} placeholder="0,00" aria-invalid={!!errors.vwlEmployeeCents} />
          {err('vwlEmployeeCents')}
        </label>
        <label className="field">
          <span>VWL Arbeitgeber € (monatlich)</span>
          <input className="input num" inputMode="decimal" value={f.vwlEmployer} onChange={(e) => set('vwlEmployer', e.target.value)} placeholder="0,00" aria-invalid={!!errors.vwlEmployerCents} />
          {err('vwlEmployerCents')}
        </label>
        <label className="field">
          <span className="invoice-label">Altersvorsorge Arbeitnehmer € (monatlich){hint('Betriebliche Altersvorsorge', 'Der Betrag, den der Mitarbeiter monatlich aus dem Bruttolohn in die betriebliche Altersvorsorge umwandelt (Entgeltumwandlung).')}</span>
          <input className="input num" inputMode="decimal" value={f.pensionEmployee} onChange={(e) => set('pensionEmployee', e.target.value)} placeholder="0,00" aria-invalid={!!errors.pensionEmployeeCents} />
          {err('pensionEmployeeCents')}
        </label>
        <label className="field">
          <span>Altersvorsorge Arbeitgeber € (monatlich)</span>
          <input className="input num" inputMode="decimal" value={f.pensionEmployer} onChange={(e) => set('pensionEmployer', e.target.value)} placeholder="0,00" aria-invalid={!!errors.pensionEmployerCents} />
          {err('pensionEmployerCents')}
        </label>
      </div>
      <label className="check">
        <input type="checkbox" checked={f.garnishment} onChange={(e) => set('garnishment', e.target.checked)} />
        <span>Pfändung oder Abtretung liegt vor{hint('Pfändung', 'Liegt ein Pfändungsbeschluss vor, muss das Lohnbüro einen Teil des Lohns an den Gläubiger überweisen. Vermerken Sie hier das Aktenzeichen und was zu beachten ist.')}</span>
      </label>
      {f.garnishment && (
        <label className="field">
          <span>Hinweis zur Pfändung</span>
          <textarea className="input textarea textarea--short" value={f.garnishmentNote} maxLength={1000} onChange={(e) => set('garnishmentNote', e.target.value)} />
        </label>
      )}
      <label className="field">
        <span>Notiz</span>
        <textarea className="input textarea textarea--short" value={f.note} maxLength={2000} onChange={(e) => set('note', e.target.value)} />
      </label>

      <div className="form-actions">
        <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Speichern'}</button>
        <button className="btn btn--secondary" type="button" onClick={onCancel}>Abbrechen</button>
      </div>
    </form>
  );
}
